import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Nav,
  Spinner,
  Tab,
  Table,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiGet, getApiBase } from '../../services/api';
import CandidateJobAlertsTab from './CandidateJobAlertsTab';
import CandidateProfileTab from './CandidateProfileTab';

const DISPLAY_LABELS = {
  active: 'Active',
  not_selected: 'Not Selected',
  hired: 'Hired',
};

function statusBadgeVariant(displayStatus) {
  if (displayStatus === 'hired') return 'success';
  if (displayStatus === 'not_selected') return 'secondary';
  return 'info';
}

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalary(min, max, currency, period) {
  const sym = currencySymbol(currency);
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const a = nf.format(Number(min));
  const b = nf.format(Number(max));
  const periodPart =
    {
      yearly: '/ year',
      annual: '/ year',
      monthly: '/ month',
      hourly: '/ hour',
      project: '',
    }[period] || '';
  return `${sym}${a}–${sym}${b} ${periodPart}`.trim();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ApplicationsTable({ applications }) {
  if (!applications.length) {
    return <p className="text-muted mb-0">No applications yet.</p>;
  }

  return (
    <div className="table-responsive">
      <Table className="rwm-candidate-dashboard__table align-middle mb-0">
        <thead>
          <tr>
            <th>Job</th>
            <th>Company</th>
            <th>Salary</th>
            <th>Status</th>
            <th>Applied</th>
          </tr>
        </thead>
        <tbody>
          {applications.map((app) => (
            <tr key={app.id}>
              <td>
                {app.job_slug ? (
                  <Link
                    to={`/jobs/${app.job_slug}`}
                    className="rwm-candidate-dashboard__job-link"
                    data-testid={`rwm-candidate-applications-job-link-${app.id}`}
                  >
                    {app.job_title}
                  </Link>
                ) : (
                  app.job_title
                )}
              </td>
              <td>{app.company_name}</td>
              <td className="rwm-candidate-dashboard__salary">
                {app.salary_min != null && app.salary_max != null
                  ? formatSalary(
                      app.salary_min,
                      app.salary_max,
                      app.salary_currency,
                      app.salary_period
                    )
                  : '—'}
              </td>
              <td>
                <Badge
                  bg={statusBadgeVariant(app.display_status)}
                  className="rwm-candidate-dashboard__status-badge"
                  data-testid={`rwm-candidate-applications-status-${app.id}`}
                >
                  {app.display_label || DISPLAY_LABELS[app.display_status] || app.display_status}
                </Badge>
              </td>
              <td>{formatDate(app.applied_at)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function filtersToQueryString(filters) {
  const params = new URLSearchParams();
  if (!filters || typeof filters !== 'object') return '';
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });
  return params.toString();
}

function SavedSearchesSection({ savedSearches, onDelete, deletingId }) {
  return (
    <div className="mt-5" data-testid="rwm-saved-searches-section">
      <h2 className="h5 mb-3 rwm-candidate-dashboard__subtitle">Saved Searches</h2>
      {!savedSearches.length ? (
        <p className="text-muted mb-0" data-testid="rwm-saved-searches-empty">
          No saved searches yet.
        </p>
      ) : (
        <div className="table-responsive">
          <Table className="rwm-candidate-dashboard__table align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Filters</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {savedSearches.map((search) => (
                <tr key={search.id}>
                  <td>{search.name}</td>
                  <td className="text-muted small">{search.filter_summary || '—'}</td>
                  <td className="text-end">
                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      <Button
                        as={Link}
                        to={`/jobs?${filtersToQueryString(search.filters)}`}
                        variant="outline-info"
                        size="sm"
                        data-testid={`rwm-saved-search-run-${search.id}`}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={deletingId === search.id}
                        onClick={() => onDelete(search.id)}
                        data-testid={`rwm-saved-search-delete-${search.id}`}
                      >
                        {deletingId === search.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SavedJobsTable({ savedJobs }) {
  if (!savedJobs.length) {
    return <p className="text-muted mb-0">No saved jobs yet.</p>;
  }

  return (
    <div className="table-responsive" data-testid="rwm-saved-jobs-table">
      <Table className="rwm-candidate-dashboard__table align-middle mb-0">
        <thead>
          <tr>
            <th>Job</th>
            <th>Company</th>
            <th>Salary</th>
            <th>Saved</th>
          </tr>
        </thead>
        <tbody>
          {savedJobs.map((job) => (
            <tr key={job.id}>
              <td>
                <Link
                  to={`/jobs/${job.slug}`}
                  className="rwm-candidate-dashboard__job-link"
                  data-testid={`rwm-candidate-saved-job-link-${job.id}`}
                >
                  {job.title}
                </Link>
              </td>
              <td>{job.company_name}</td>
              <td className="rwm-candidate-dashboard__salary">
                {formatSalary(
                  job.salary_min,
                  job.salary_max,
                  job.salary_currency,
                  job.salary_period
                )}
              </td>
              <td>{formatDate(job.saved_at)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default function CandidateDashboard() {
  const { user, token, authLoading, isVerified } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [deletingSearchId, setDeletingSearchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState('');

  const handleAuthError = useCallback(
    (res) => {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return true;
      }
      if (res.status === 403) {
        setAccessDenied(true);
        return true;
      }
      return false;
    },
    [navigate]
  );

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    setAccessDenied(false);
    try {
      const [appsRes, savedRes, searchesRes] = await Promise.all([
        apiGet('/api/v1/candidate/applications', token),
        apiGet('/api/v1/candidate/saved-jobs', token),
        apiGet('/api/v1/candidate/saved-searches', token),
      ]);
      if (handleAuthError(appsRes) || handleAuthError(savedRes) || handleAuthError(searchesRes)) {
        return;
      }
      if (!appsRes.ok || !savedRes.ok || !searchesRes.ok) {
        setError('Could not load dashboard data. Please try again.');
        return;
      }
      const appsData = await appsRes.json();
      const savedData = await savedRes.json();
      const searchesData = await searchesRes.json();
      setApplications(appsData.data ?? []);
      setSavedJobs(savedData.data ?? []);
      setSavedSearches(searchesData.data ?? []);
    } catch {
      setError('Could not load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  const handleDeleteSavedSearch = useCallback(
    async (searchId) => {
      if (!token) return;
      setDeletingSearchId(searchId);
      try {
        const res = await fetch(`${getApiBase()}/api/v1/candidate/saved-searches/${searchId}`, {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (handleAuthError(res)) return;
        if (!res.ok) return;
        setSavedSearches((prev) => prev.filter((s) => s.id !== searchId));
      } finally {
        setDeletingSearchId(null);
      }
    },
    [token, handleAuthError]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!token || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'candidate') {
      if (user.role === 'company') {
        navigate('/company/dashboard', { replace: true });
        return;
      }
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadDashboard();
  }, [authLoading, token, user, navigate, loadDashboard]);

  if (authLoading || (loading && !accessDenied)) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" data-testid="rwm-candidate-dashboard-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container className="py-5">
        <Alert variant="danger" data-testid="rwm-candidate-dashboard-access-denied">
          Access denied. This page is for candidate accounts only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 rwm-candidate-dashboard">
      <h1 className="h3 mb-4 rwm-candidate-dashboard__title">My Dashboard</h1>

      {!isVerified ? (
        <Alert variant="warning" className="mb-4">
          Your email is not verified. Check your inbox or{' '}
          <Link to="/verify-email-pending" className="alert-link">
            resend the verification email
          </Link>
          .
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="mb-4" data-testid="rwm-candidate-dashboard-error">
          {error}
        </Alert>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav variant="tabs" className="rwm-candidate-dashboard__nav mb-4">
          <Nav.Item>
            <Nav.Link eventKey="applications" data-testid="rwm-candidate-tab-applications">
              My Applications
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="saved" data-testid="rwm-candidate-tab-saved">
              Saved Jobs
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="alerts" data-testid="rwm-candidate-tab-alerts">
              Job Alerts
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="profile" data-testid="rwm-candidate-tab-profile">
              My Profile
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="applications">
            <ApplicationsTable applications={applications} />
          </Tab.Pane>
          <Tab.Pane eventKey="saved">
            <SavedJobsTable savedJobs={savedJobs} />
            <SavedSearchesSection
              savedSearches={savedSearches}
              onDelete={handleDeleteSavedSearch}
              deletingId={deletingSearchId}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="alerts">
            <CandidateJobAlertsTab
              token={token}
              isActive={activeTab === 'alerts'}
              handleAuthError={handleAuthError}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="profile">
            <CandidateProfileTab token={token} handleAuthError={handleAuthError} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
}
